import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Copy,
  Check,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  FileSpreadsheet,
  Share2,
  Link,
  TestTube,
  AlertTriangle,
  Pencil,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  googleSheetsApi,
  GoogleSheetsIntegrationDto,
  GoogleSheetsServiceAccountInfo,
  GoogleSheetsTestResult,
  SheetInfo,
} from '@/lib/api';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Google Sheets brand icon
const GoogleSheetsIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" fill="#0F9D58"/>
    <path d="M14.5 2v5.5H20" fill="#87CEAB"/>
    <path d="M8 13h8v1H8v-1zm0 3h8v1H8v-1zm0-6h8v1H8v-1z" fill="white"/>
  </svg>
);

type WizardStep = 'url' | 'share' | 'test' | 'complete';

interface StepStatus {
  url: 'pending' | 'complete';
  share: 'pending' | 'complete';
  test: 'pending' | 'complete';
}

export default function GoogleSheetsIntegration() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Data states
  const [integration, setIntegration] = useState<GoogleSheetsIntegrationDto | null>(null);
  const [serviceAccount, setServiceAccount] = useState<GoogleSheetsServiceAccountInfo | null>(null);
  const [testResult, setTestResult] = useState<GoogleSheetsTestResult | null>(null);
  const [availableSheets, setAvailableSheets] = useState<SheetInfo[]>([]);
  
  // UI states
  const [copied, setCopied] = useState(false);
  const [currentStep, setCurrentStep] = useState<WizardStep>('url');
  const [stepStatus, setStepStatus] = useState<StepStatus>({
    url: 'pending',
    share: 'pending',
    test: 'pending',
  });
  
  // Form state
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [sheetName, setSheetName] = useState('');
  const [spreadsheetName, setSpreadsheetName] = useState('');
  const [enabled, setEnabled] = useState(false);
  
  // Dialog state
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  
  // Edit sheet state
  const [isEditingSheet, setIsEditingSheet] = useState(false);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [integrationData, serviceAccountData] = await Promise.all([
        googleSheetsApi.getIntegration(),
        googleSheetsApi.getServiceAccountInfo(),
      ]);

      setServiceAccount(serviceAccountData);

      if (integrationData) {
        setIntegration(integrationData);
        if (integrationData.spreadsheetId) {
          setSpreadsheetUrl(`https://docs.google.com/spreadsheets/d/${integrationData.spreadsheetId}/edit`);
          
          // Fetch spreadsheet metadata (name, available sheets) for existing integration
          try {
            const testResult = await googleSheetsApi.testConnection({
              spreadsheetUrl: `https://docs.google.com/spreadsheets/d/${integrationData.spreadsheetId}/edit`,
            });
            if (testResult.success) {
              if (testResult.spreadsheetName) {
                setSpreadsheetName(testResult.spreadsheetName);
              }
              if (testResult.availableSheets) {
                setAvailableSheets(testResult.availableSheets);
              }
            }
          } catch (metadataError) {
            console.warn('Failed to fetch spreadsheet metadata:', metadataError);
          }
        }
        setSheetName(integrationData.sheetName || '');
        setEnabled(integrationData.enabled);
        
        // If we have a complete integration, go to complete step
        if (integrationData.spreadsheetId && integrationData.sheetName) {
          setCurrentStep('complete');
          setStepStatus({
            url: 'complete',
            share: 'complete',
            test: 'complete',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load Google Sheets integration:', error);
      toast.error(t('googleSheets.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const extractSpreadsheetId = (input: string): string => {
    const urlMatch = input.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }
    return input.trim();
  };

  const handleCopyEmail = async () => {
    if (serviceAccount?.email) {
      await navigator.clipboard.writeText(serviceAccount.email);
      setCopied(true);
      toast.success(t('googleSheets.emailCopied'));
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openGoogleSheetsWithShare = () => {
    if (spreadsheetUrl) {
      const id = extractSpreadsheetId(spreadsheetUrl);
      window.open(`https://docs.google.com/spreadsheets/d/${id}/edit`, '_blank');
    }
  };

  // Step 1: Validate URL and proceed
  const handleStep1Complete = () => {
    if (!spreadsheetUrl) {
      toast.error(t('googleSheets.spreadsheetRequired'));
      return;
    }
    
    const id = extractSpreadsheetId(spreadsheetUrl);
    if (!id || id.length < 20) {
      toast.error(t('googleSheets.invalidUrl'));
      return;
    }
    
    setStepStatus(prev => ({ ...prev, url: 'complete' }));
    setCurrentStep('share');
  };

  // Step 2: Mark as done and proceed to test
  const handleStep2Complete = () => {
    setStepStatus(prev => ({ ...prev, share: 'complete' }));
    setCurrentStep('test');
  };

  // Step 3: Test connection
  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setAvailableSheets([]);

      const result = await googleSheetsApi.testConnection({
        spreadsheetUrl: spreadsheetUrl,
      });
      
      setTestResult(result);

      if (result.success) {
        // Save spreadsheet name
        if (result.spreadsheetName) {
          setSpreadsheetName(result.spreadsheetName);
        }
        if (result.availableSheets && result.availableSheets.length > 0) {
          setAvailableSheets(result.availableSheets);
          // Auto-select first sheet
          setSheetName(result.availableSheets[0].name);
        }
        toast.success(t('googleSheets.testSuccess'));
      } else {
        toast.error(result.error || t('googleSheets.testFailed'));
      }
    } catch (error) {
      console.error('Failed to test connection:', error);
      setTestResult({
        success: false,
        error: t('googleSheets.testError'),
      });
      toast.error(t('googleSheets.testError'));
    } finally {
      setTesting(false);
    }
  };

  // Step 3: Confirm sheet selection and save
  const handleStep3Complete = async () => {
    try {
      setSaving(true);
      const extractedId = extractSpreadsheetId(spreadsheetUrl);
      
      const result = await googleSheetsApi.createOrUpdateIntegration({
        enabled: true,
        spreadsheetId: extractedId,
        sheetName: sheetName,
      });

      setIntegration(result);
      setEnabled(true);
      setStepStatus(prev => ({ ...prev, test: 'complete' }));
      setCurrentStep('complete');
      toast.success(t('googleSheets.saveSuccess'));
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(t('googleSheets.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = (newEnabled: boolean) => {
    if (!newEnabled) {
      // Show confirmation dialog before disabling
      setShowDisableDialog(true);
    } else {
      // Enable without confirmation
      performToggle(true);
    }
  };

  const performToggle = async (newEnabled: boolean) => {
    try {
      setSaving(true);
      if (newEnabled) {
        await googleSheetsApi.updateIntegration({ enabled: true });
        setEnabled(true);
        toast.success(t('googleSheets.enabled'));
      } else {
        // Disable = delete the integration
        await googleSheetsApi.deleteIntegration();
        setIntegration(null);
        setSpreadsheetUrl('');
        setSheetName('');
        setEnabled(false);
        setTestResult(null);
        setAvailableSheets([]);
        setStepStatus({ url: 'pending', share: 'pending', test: 'pending' });
        setCurrentStep('url');
        toast.success(t('googleSheets.disconnected'));
      }
    } catch (error) {
      console.error('Failed to toggle:', error);
      toast.error(t('googleSheets.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDisable = () => {
    setShowDisableDialog(false);
    performToggle(false);
  };

  const handleStartOver = () => {
    setTestResult(null);
    setAvailableSheets([]);
    setSheetName('');
    setStepStatus({ url: 'pending', share: 'pending', test: 'pending' });
    setCurrentStep('url');
  };

  const handleEditSheet = async () => {
    // If we don't have available sheets, fetch them
    if (availableSheets.length === 0 && integration?.spreadsheetId) {
      try {
        setLoadingSheets(true);
        const result = await googleSheetsApi.testConnection({
          spreadsheetUrl: `https://docs.google.com/spreadsheets/d/${integration.spreadsheetId}/edit`,
        });
        if (result.success && result.availableSheets) {
          setAvailableSheets(result.availableSheets);
          if (result.spreadsheetName) {
            setSpreadsheetName(result.spreadsheetName);
          }
        }
      } catch (error) {
        console.error('Failed to fetch sheets:', error);
        toast.error(t('googleSheets.loadError'));
        return;
      } finally {
        setLoadingSheets(false);
      }
    }
    setSelectedSheet(sheetName);
    setIsEditingSheet(true);
  };

  const handleSaveSheet = async () => {
    if (!selectedSheet || selectedSheet === integration?.sheetName) {
      setIsEditingSheet(false);
      return;
    }
    
    try {
      setSaving(true);
      const result = await googleSheetsApi.updateIntegration({
        sheetName: selectedSheet,
      });
      setIntegration(result);
      setSheetName(selectedSheet);
      setIsEditingSheet(false);
      toast.success(t('googleSheets.sheetUpdated'));
    } catch (error) {
      console.error('Failed to update sheet:', error);
      toast.error(t('googleSheets.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditSheet = () => {
    setSelectedSheet('');
    setIsEditingSheet(false);
  };

  if (loading) {
    return (
      <Card className="p-6 card-gradient">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  const steps = [
    { id: 'url', label: t('googleSheets.stepUrl'), icon: Link },
    { id: 'share', label: t('googleSheets.stepShare'), icon: Share2 },
    { id: 'test', label: t('googleSheets.stepTest'), icon: TestTube },
  ];

  return (
    <Card className="p-6 card-gradient">
      <div className="space-y-6">
        {/* Header */}
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <GoogleSheetsIcon className="h-8 w-8" />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h2 className="text-xl font-semibold">{t('googleSheets.title')}</h2>
              <p className="text-sm text-muted-foreground">{t('googleSheets.description')}</p>
            </div>
          </div>
          {currentStep === 'complete' && (
            <Badge variant={enabled ? "default" : "secondary"} className={enabled ? "bg-green-600" : ""}>
              {enabled ? (
                <>
                  <CheckCircle2 className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                  {t('googleSheets.connected')}
                </>
              ) : (
                t('googleSheets.disabled')
              )}
            </Badge>
          )}
        </div>

        {/* Progress Steps - Only show during wizard */}
        {currentStep !== 'complete' && (
          <>
            <div className="flex items-center justify-center gap-0" dir={isRTL ? 'rtl' : 'ltr'}>
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = step.id === currentStep;
                const isComplete = stepStatus[step.id as keyof StepStatus] === 'complete';
                const prevStepComplete = index > 0 && stepStatus[steps[index - 1].id as keyof StepStatus] === 'complete';
                
                return (
                  <div key={step.id} className="flex items-center">
                    {/* Line before step (except first) */}
                    {index > 0 && (
                      <div className={`w-16 sm:w-24 md:w-32 h-0.5 ${prevStepComplete ? 'bg-green-600' : 'bg-muted'}`} />
                    )}
                    
                    {/* Step circle and label */}
                    <div className="flex flex-col items-center gap-2">
                      <div 
                        className={`
                          flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all
                          ${isComplete ? 'bg-green-600 border-green-600 text-white' : ''}
                          ${isActive && !isComplete ? 'bg-primary border-primary text-primary-foreground' : ''}
                          ${!isActive && !isComplete ? 'bg-muted border-muted-foreground/30 text-muted-foreground' : ''}
                        `}
                      >
                        {isComplete ? (
                          <Check className="h-5 w-5" />
                        ) : (
                          <StepIcon className="h-5 w-5" />
                        )}
                      </div>
                      <span className={`text-xs font-medium text-center ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                        {step.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <Separator />
          </>
        )}

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Enter URL */}
          {currentStep === 'url' && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-primary" />
                <h3 className="text-lg font-semibold">{t('googleSheets.step1Title')}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {t('googleSheets.step1Description')}
                </p>
              </div>

              <div className="max-w-lg mx-auto space-y-4">
                <div className={`space-y-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <Label htmlFor="spreadsheet-url">{t('googleSheets.spreadsheetUrl')}</Label>
                  <Input
                    id="spreadsheet-url"
                    placeholder={t('googleSheets.spreadsheetUrlPlaceholder')}
                    value={spreadsheetUrl}
                    onChange={(e) => setSpreadsheetUrl(e.target.value)}
                    dir="ltr"
                    className="text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('googleSheets.spreadsheetUrlHelp')}
                  </p>
                </div>

                <Button 
                  onClick={handleStep1Complete}
                  className="w-full"
                  disabled={!spreadsheetUrl}
                >
                  {t('googleSheets.continue')}
                  {isRTL ? (
                    <ArrowLeft className="h-4 w-4 me-2" />
                  ) : (
                    <ArrowRight className="h-4 w-4 ms-2" />
                  )}
                </Button>

                {/* Create New Sheet Link */}
                <div className="text-center pt-2">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('googleSheets.noSheetYet')}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => window.open('https://sheets.google.com/create', '_blank')}
                    className="gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t('googleSheets.createNewSheet')}
                  </Button>
                </div>

                {/* Schema Information */}
                <div className={`rounded-lg border bg-muted/30 p-4 mt-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <h4 className="text-sm font-medium mb-2">
                    {t('googleSheets.schemaTitle')}
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    {t('googleSheets.schemaDescription')}
                  </p>
                  
                  {/* Column list - RTL: F→A, LTR: A→F */}
                  <div className="space-y-1.5 text-xs">
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-mono font-bold text-primary w-4">A</span>
                      <span>{t('googleSheets.colDate')}</span>
                    </div>
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-mono font-bold text-primary w-4">B</span>
                      <span>{t('googleSheets.colYear')}</span>
                    </div>
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-mono font-bold text-primary w-4">C</span>
                      <span>{t('googleSheets.colMonth')}</span>
                    </div>
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-mono font-bold text-primary w-4">D</span>
                      <span>{t('googleSheets.colCategory')}</span>
                    </div>
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-mono font-bold text-primary w-4">E</span>
                      <span>{t('googleSheets.colAmount')}</span>
                    </div>
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-mono font-bold text-primary w-4">F</span>
                      <span>{t('googleSheets.colDescription')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Share with Service Account */}
          {currentStep === 'share' && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <Share2 className="h-12 w-12 mx-auto text-primary" />
                <h3 className="text-lg font-semibold">{t('googleSheets.step2Title')}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {t('googleSheets.step2Description')}
                </p>
              </div>

              <div className="max-w-lg mx-auto space-y-4">
                {serviceAccount && serviceAccount.email !== 'Not configured - please contact support' ? (
                  <>
                    {/* Email to copy */}
                    <div className="rounded-lg border-2 border-primary/20 bg-primary/5 p-4 space-y-3">
                      <p className="text-sm font-medium text-center">
                        {t('googleSheets.addThisEmail')}
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 rounded bg-background border px-3 py-2 text-sm font-mono break-all font-semibold text-center" dir="ltr">
                          {serviceAccount.email}
                        </code>
                        <Button
                          variant={copied ? "default" : "outline"}
                          size="sm"
                          onClick={handleCopyEmail}
                          className={`shrink-0 ${copied ? 'bg-green-600 hover:bg-green-700' : ''}`}
                        >
                          {copied ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Instructions */}
                    <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      <AlertTitle className="text-blue-800 dark:text-blue-200">{t('googleSheets.howToShare')}</AlertTitle>
                      <AlertDescription className="text-blue-700 dark:text-blue-300">
                        <ol className="list-decimal ms-4 mt-2 space-y-1 text-sm">
                          <li>{t('googleSheets.shareStep1')}</li>
                          <li>{t('googleSheets.shareStep2')}</li>
                          <li>{t('googleSheets.shareStep3')}</li>
                          <li>{t('googleSheets.shareStep4')}</li>
                        </ol>
                      </AlertDescription>
                    </Alert>

                    <Button
                      variant="outline"
                      onClick={openGoogleSheetsWithShare}
                      className="w-full"
                    >
                      <ExternalLink className="h-4 w-4 me-2" />
                      {t('googleSheets.openAndShare')}
                    </Button>

                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => setCurrentStep('url')}
                        className="flex-1"
                      >
                        {isRTL ? (
                          <ArrowRight className="h-4 w-4 me-2" />
                        ) : (
                          <ArrowLeft className="h-4 w-4 me-2" />
                        )}
                        {t('googleSheets.back')}
                      </Button>
                      <Button 
                        onClick={handleStep2Complete}
                        className="flex-1"
                      >
                        {t('googleSheets.iSharedIt')}
                        {isRTL ? (
                          <ArrowLeft className="h-4 w-4 ms-2" />
                        ) : (
                          <ArrowRight className="h-4 w-4 ms-2" />
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <Alert className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800 dark:text-red-200">{t('googleSheets.notConfigured')}</AlertTitle>
                    <AlertDescription className="text-red-700 dark:text-red-300">
                      {t('googleSheets.contactSupport')}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Test Connection */}
          {currentStep === 'test' && (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <TestTube className="h-12 w-12 mx-auto text-primary" />
                <h3 className="text-lg font-semibold">{t('googleSheets.step3Title')}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {t('googleSheets.step3Description')}
                </p>
              </div>

              <div className="max-w-lg mx-auto space-y-4">
                {!testResult && (
                  <Button
                    onClick={handleTestConnection}
                    disabled={testing}
                    className="w-full"
                    size="lg"
                  >
                    {testing ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin me-2" />
                        {t('googleSheets.testing')}
                      </>
                    ) : (
                      <>
                        <TestTube className="h-5 w-5 me-2" />
                        {t('googleSheets.testConnection')}
                      </>
                    )}
                  </Button>
                )}

                {/* Test Result */}
                {testResult && (
                  <>
                    <Alert className={testResult.success 
                      ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950" 
                      : "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"
                    }>
                      {testResult.success ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                      <AlertTitle className={testResult.success ? "text-green-800 dark:text-green-200" : "text-red-800 dark:text-red-200"}>
                        {testResult.success ? t('googleSheets.testSuccessTitle') : t('googleSheets.testFailedTitle')}
                      </AlertTitle>
                      <AlertDescription className={testResult.success ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"}>
                        {testResult.success ? (
                          <p>{t('googleSheets.spreadsheetFound')}: <strong>{testResult.spreadsheetName}</strong></p>
                        ) : (
                          <p>{testResult.error}</p>
                        )}
                      </AlertDescription>
                    </Alert>

                    {testResult.success && availableSheets.length > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="sheet-select">{t('googleSheets.selectSheet')}</Label>
                        {availableSheets.length === 1 ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 rounded border bg-muted/50 px-3 py-2 text-sm font-medium" dir="ltr">
                              {availableSheets[0].name}
                            </div>
                            <Badge variant="secondary">{t('googleSheets.onlySheet')}</Badge>
                          </div>
                        ) : (
                          <Select value={sheetName} onValueChange={setSheetName}>
                            <SelectTrigger id="sheet-select" dir="ltr">
                              <SelectValue placeholder={t('googleSheets.selectSheetPlaceholder')} />
                            </SelectTrigger>
                            <SelectContent>
                              {availableSheets.map((sheet) => (
                                <SelectItem key={sheet.sheetId} value={sheet.name}>
                                  {sheet.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {t('googleSheets.sheetNameHelp')}
                        </p>
                      </div>
                    )}

                    {!testResult.success && (
                      <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentStep('share')}
                        className="flex-1"
                      >
                        {isRTL ? (
                          <ArrowRight className="h-4 w-4 me-2" />
                        ) : (
                          <ArrowLeft className="h-4 w-4 me-2" />
                        )}
                        {t('googleSheets.backToShare')}
                      </Button>
                        <Button
                          onClick={handleTestConnection}
                          disabled={testing}
                          className="flex-1"
                        >
                          {t('googleSheets.tryAgain')}
                        </Button>
                      </div>
                    )}

                    {testResult.success && sheetName && (
                      <Button 
                        onClick={handleStep3Complete}
                        disabled={saving}
                        className="w-full"
                        size="lg"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin me-2" />
                            {t('googleSheets.saving')}
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-5 w-5 me-2" />
                            {t('googleSheets.completeSetup')}
                          </>
                        )}
                      </Button>
                    )}
                  </>
                )}

                {!testResult && (
                  <Button
                    variant="ghost"
                    onClick={() => setCurrentStep('share')}
                    className="w-full"
                  >
                    {isRTL ? (
                      <ArrowRight className="h-4 w-4 me-2" />
                    ) : (
                      <ArrowLeft className="h-4 w-4 me-2" />
                    )}
                    {t('googleSheets.back')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Complete State */}
          {currentStep === 'complete' && integration && (
            <div className="space-y-6">
              {/* Enable/Disable Toggle - First */}
              <div className={`flex items-center justify-between rounded-lg border p-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`space-y-0.5 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <Label htmlFor="enable-integration" className="font-medium">
                    {t('googleSheets.enableIntegration')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('googleSheets.enableDescription')}
                  </p>
                </div>
                <Switch
                  id="enable-integration"
                  checked={enabled}
                  onCheckedChange={handleToggleEnabled}
                  disabled={saving}
                />
              </div>

              {/* Success Alert */}
              <Alert className={`border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <AlertTitle className="text-green-800 dark:text-green-200 text-lg">
                    {t('googleSheets.integrationComplete')}
                  </AlertTitle>
                  <AlertDescription className="text-green-700 dark:text-green-300 space-y-2">
                    <p>{t('googleSheets.integrationCompleteDesc')}</p>
                    <ul className={`text-sm mt-2 space-y-1 ${isRTL ? 'list-disc mr-4' : 'list-disc ml-4'}`}>
                      <li>{t('googleSheets.syncManualTransactions')}</li>
                      <li>{t('googleSheets.syncRecurringPayments')}</li>
                      <li>{t('googleSheets.syncWhatsAppTransactions')}</li>
                    </ul>
                  </AlertDescription>
                </div>
              </Alert>

              {/* Integration Details */}
              <div className="rounded-lg border p-4 space-y-3">
                <h4 className={`font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('googleSheets.integrationDetails')}
                </h4>
                <div className="space-y-3 text-sm">
                  {/* Spreadsheet Name */}
                  {spreadsheetName && (
                    <div className={`flex gap-4 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                      <span className="text-muted-foreground shrink-0 w-24">
                        {isRTL ? `:${t('googleSheets.fileName')}` : `${t('googleSheets.fileName')}:`}
                      </span>
                      <span className="font-medium">{spreadsheetName}</span>
                    </div>
                  )}
                  
                  {/* Spreadsheet ID */}
                  <div className={`flex gap-4 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                    <span className="text-muted-foreground shrink-0 w-24">
                      {isRTL ? `:${t('googleSheets.spreadsheet')}` : `${t('googleSheets.spreadsheet')}:`}
                    </span>
                    <span className="font-medium font-mono text-xs break-all flex-1" dir="ltr">
                      {integration.spreadsheetId}
                    </span>
                  </div>
                  
                  {/* Sheet Name - Editable */}
                  <div className={`flex gap-4 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                    <span className="text-muted-foreground shrink-0 w-24">
                      {isRTL ? `:${t('googleSheets.sheetName')}` : `${t('googleSheets.sheetName')}:`}
                    </span>
                    
                    {isEditingSheet ? (
                      <div className={`flex items-center gap-2 flex-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        {loadingSheets ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                              <SelectTrigger className="w-[180px] h-8" dir="ltr">
                                <SelectValue placeholder={t('googleSheets.selectSheetPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent>
                                {availableSheets.map((sheet) => (
                                  <SelectItem key={sheet.sheetId} value={sheet.name}>
                                    {sheet.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleSaveSheet}
                              disabled={saving || !selectedSheet}
                              className="h-8 w-8 p-0"
                            >
                              {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleCancelEditSheet}
                              disabled={saving}
                              className="h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className={`flex items-center gap-2 flex-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="font-medium" dir="ltr">{integration.sheetName}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleEditSheet}
                          className="h-6 w-6 p-0"
                          title={t('googleSheets.changeSheet')}
                        >
                          <Pencil className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Open Spreadsheet Button */}
              <Button
                variant="outline"
                onClick={openGoogleSheetsWithShare}
                className="w-full"
              >
                <ExternalLink className="h-4 w-4 me-2" />
                {t('googleSheets.openSpreadsheet')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Disable Confirmation Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent dir={isRTL ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('googleSheets.disableDialogTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('googleSheets.disableDialogDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {serviceAccount && (
            <div className="rounded-lg border bg-muted/50 p-3">
              <p className="text-sm font-medium text-foreground mb-2">
                {t('googleSheets.disableDialogRemoveEmail')}
              </p>
              <code className="text-xs bg-background px-2 py-1 rounded border" dir="ltr">
                {serviceAccount.email}
              </code>
            </div>
          )}
          <AlertDialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <AlertDialogCancel>{t('googleSheets.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDisable}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('googleSheets.confirmDisable')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
